import { authentication } from '@microsoft/teams-js';
import { teamsPaths } from '@teams/routes/AppRoutes';
import { Button, Input } from '@toggle/design-system';
import React, { ChangeEvent, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PasswordInput } from '~/components/forms/password-input/PasswordInput';
import { useEmailValidation } from '~/hooks/use-email-validation/useEmailValidation';
import { validateCurrentPassword } from '~/utils/password-validation-utils/password-validation-utils';
import { postLogin } from '~/views/auth/services/auth-services';

import * as S from './AuthPopup.styles';

const defaultState = { email: '', password: '' };

export const AuthPopup = () => {
  const { t } = useTranslation('auth');
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState(defaultState);
  const [apiError, setApiError] = useState('');
  const [processing, setProcessing] = useState(false);

  const updateErrors = (name: string, value: string | undefined) =>
    setErrors(e => ({ ...e, [name]: value ?? '' }));

  const { clearEmailError, validateEmail } = useEmailValidation(error =>
    updateErrors('email', error)
  );

  const clearError = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'email') {
      clearEmailError();
    } else {
      updateErrors(e.target.name, '');
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setApiError('');
    setData(d => ({ ...d, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setProcessing(true);

    const passwordError = validateCurrentPassword(data.password);
    updateErrors('password', passwordError);

    const isEmailValid = await validateEmail(data.email, true);

    if (!isEmailValid || passwordError) {
      setProcessing(false);
      return;
    }

    try {
      const response = await postLogin({
        username: data.email,
        password: data.password,
      });

      if (response.error) {
        setApiError(t('auth:login.incorrectCredentialsLabel'));
        return;
      }

      if (window.opener) {
        // Inside the Teams auth popup — notify the SDK so the tab's
        // successCallback can receive the payload and call initStores().
        authentication.notifySuccess(JSON.stringify(response.data));
      } else {
        // Direct browser navigation (Root.tsx redirected here) — full reload
        // so Root re-runs getSession() and bootstraps the fresh session.
        window.location.assign(teamsPaths.alfred);
      }
    } catch {
      setApiError(t('auth:login.incorrectCredentialsLabel'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <S.Wrapper>
      <S.Container>
        <S.Title>{t('auth:login.loginHeader')}</S.Title>
        <S.Form onSubmit={handleSubmit}>
          <Input
            id="email"
            type="email"
            label={t('auth:login.emailText')}
            value={data.email}
            name="email"
            onChange={handleChange}
            onFocus={clearError}
            data-testid="email-input"
            hasError={!!errors.email}
            /* i18next-extract-disable-next-line */
            errorText={t(errors.email)}
          />
          <PasswordInput
            id="password"
            label={t('auth:login.passwordText')}
            value={data.password}
            name="password"
            onChange={handleChange}
            onFocus={clearError}
            data-testid="password-input"
            hasError={!!errors.password || !!apiError}
            /* i18next-extract-disable-next-line */
            errorText={t(errors.password) || apiError}
          />
          {apiError && <S.ErrorMessage>{apiError}</S.ErrorMessage>}
          <Button
            fullWidth
            type="submit"
            disabled={processing}
            isLoading={processing}
            label={t('auth:login.loginButtonLabel')}
            data-testid="log-in-btn"
            size="large"
          />
        </S.Form>
      </S.Container>
    </S.Wrapper>
  );
};
